(() => {
  const TR_LOCAL_LEN = 10;
  const LOCAL_MIN = 6;
  const LOCAL_MAX = 12;

  const DIAL_CODES = [
    "+994",
    "+971",
    "+966",
    "+90",
    "+81",
    "+61",
    "+49",
    "+44",
    "+43",
    "+41",
    "+39",
    "+34",
    "+33",
    "+32",
    "+31",
    "+7",
    "+1",
  ].sort((a, b) => b.length - a.length);

  function onlyDigits(value) {
    return String(value || "").replace(/\D/g, "");
  }

  function stripDigits(value) {
    return String(value || "").replace(/\d+/g, "");
  }

  function formatTrLocal(digits) {
    const d = digits.slice(0, TR_LOCAL_LEN);
    const parts = [];
    if (d.length > 0) parts.push(d.slice(0, 3));
    if (d.length > 3) parts.push(d.slice(3, 6));
    if (d.length > 6) parts.push(d.slice(6, 8));
    if (d.length > 8) parts.push(d.slice(8, 10));
    return parts.join(" ");
  }

  function formatGenericLocal(digits) {
    const d = digits.slice(0, LOCAL_MAX);
    return d.replace(/(\d{3})(?=\d)/g, "$1 ").trim();
  }

  function formatLocal(digits, dial) {
    if (!digits) return "";
    if (dial === "+90") return formatTrLocal(digits);
    return formatGenericLocal(digits);
  }

  function detectDialAndLocal(raw) {
    let digits = onlyDigits(raw);
    if (digits.startsWith("00")) digits = digits.slice(2);

    for (const code of DIAL_CODES) {
      const codeDigits = onlyDigits(code);
      if (digits.startsWith(codeDigits) && digits.length > codeDigits.length) {
        return {
          dial: code,
          local: digits.slice(codeDigits.length),
        };
      }
    }

    // TR numbers often typed as 05xx...
    if (digits.startsWith("0") && digits.length >= 11) {
      return { dial: "+90", local: digits.slice(1) };
    }

    return { dial: null, local: digits };
  }

  function setFieldError(input, message) {
    const wrap = input.closest(".contact-page__field");
    const errorEl =
      wrap?.querySelector("[data-field-error]") ||
      document.getElementById("ContactForm-phone-error");
    const hasError = Boolean(message);

    input.setAttribute("aria-invalid", hasError ? "true" : "false");
    input.classList.toggle("contact-page__input--invalid", hasError);

    if (!errorEl) return;
    if (hasError) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    } else {
      errorEl.textContent = "";
      errorEl.hidden = true;
    }
  }

  function initNoDigitsField(input) {
    const clean = () => {
      const next = stripDigits(input.value);
      if (next !== input.value) input.value = next;
      if (/\d/.test(input.value)) {
        setFieldError(input, (window.hrcI18n?.contactNoDigits || "Numbers are not allowed in this field."));
      } else {
        setFieldError(input, "");
      }
    };

    input.addEventListener("beforeinput", (event) => {
      if (event.inputType?.startsWith("delete")) return;
      const data = event.data || "";
      if (/\d/.test(data)) event.preventDefault();
    });

    input.addEventListener("keydown", (event) => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (event.key.length === 1 && /\d/.test(event.key)) {
        event.preventDefault();
        setFieldError(input, (window.hrcI18n?.contactNoDigits || "Numbers are not allowed in this field."));
      }
    });

    input.addEventListener("input", clean);
    input.addEventListener("paste", (event) => {
      event.preventDefault();
      const pasted =
        (event.clipboardData || window.clipboardData)?.getData("text") || "";
      const start = input.selectionStart ?? input.value.length;
      const end = input.selectionEnd ?? input.value.length;
      const next = stripDigits(
        input.value.slice(0, start) + pasted + input.value.slice(end),
      );
      input.value = next;
      setFieldError(
        input,
        /\d/.test(pasted) ? (window.hrcI18n?.contactNoDigits || "Numbers are not allowed in this field.") : "",
      );
    });

    input.addEventListener("blur", clean);

    if (input.value) clean();
  }

  function initPhoneField(root) {
    const dialEl = root.querySelector("[data-phone-dial]");
    const localEl = root.querySelector("[data-phone-local]");
    const fullEl = root.querySelector("[data-phone-full]");
    const form = root.closest("form");
    if (!dialEl || !localEl || !fullEl) return;

    function currentDial() {
      return dialEl.value || "+90";
    }

    function syncFull() {
      const dial = currentDial();
      const digits = onlyDigits(localEl.value);
      fullEl.value = digits ? `${dial} ${formatLocal(digits, dial)}`.trim() : "";
      return digits;
    }

    function applyLocal(rawDigits) {
      let digits = onlyDigits(rawDigits);
      const dial = currentDial();

      if (dial === "+90") {
        if (digits.startsWith("0")) digits = digits.slice(1);
        digits = digits.slice(0, TR_LOCAL_LEN);
      } else {
        digits = digits.slice(0, LOCAL_MAX);
      }

      localEl.value = formatLocal(digits, dial);
      syncFull();
      return digits;
    }

    function hydrateFromStored(raw) {
      if (!raw) return;
      const detected = detectDialAndLocal(raw);
      if (detected.dial) {
        const option = Array.from(dialEl.options).find(
          (o) => o.value === detected.dial,
        );
        if (option) dialEl.value = detected.dial;
      }
      applyLocal(detected.local);
    }

    dialEl.addEventListener("change", () => {
      applyLocal(localEl.value);
      setFieldError(localEl, "");
    });

    localEl.addEventListener("input", () => {
      const detected = detectDialAndLocal(localEl.value);
      // If user pastes/types a full international number into local field
      if (
        detected.dial &&
        onlyDigits(localEl.value).startsWith(onlyDigits(detected.dial))
      ) {
        const option = Array.from(dialEl.options).find(
          (o) => o.value === detected.dial,
        );
        if (option) dialEl.value = detected.dial;
        applyLocal(detected.local);
      } else {
        applyLocal(localEl.value);
      }
      setFieldError(localEl, "");
    });

    localEl.addEventListener("keydown", (event) => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      const allowed = [
        "Backspace",
        "Delete",
        "Tab",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
      ];
      if (allowed.includes(event.key)) return;
      if (!/^\d$/.test(event.key)) event.preventDefault();
    });

    localEl.addEventListener("paste", (event) => {
      event.preventDefault();
      const pasted =
        (event.clipboardData || window.clipboardData)?.getData("text") || "";
      const detected = detectDialAndLocal(pasted);
      if (detected.dial) {
        const option = Array.from(dialEl.options).find(
          (o) => o.value === detected.dial,
        );
        if (option) dialEl.value = detected.dial;
      }
      applyLocal(detected.local);
      setFieldError(localEl, "");
    });

    localEl.addEventListener("blur", () => {
      const digits = applyLocal(localEl.value);
      if (!digits.length) {
        setFieldError(localEl, "");
        return;
      }

      const dial = currentDial();
      const expected = dial === "+90" ? TR_LOCAL_LEN : null;
      if (expected && digits.length !== expected) {
        setFieldError(
          localEl,
          window.hrcI18n?.phoneTrError ||
            "Enter a 10-digit number for Turkey (e.g. 505 555 55 55).",
        );
        return;
      }
      if (digits.length < LOCAL_MIN || digits.length > LOCAL_MAX) {
        setFieldError(
          localEl,
          (window.hrcI18n?.phoneGenericError || "Enter a valid phone number (__MIN__–__MAX__ digits).")
            .replace("__MIN__", String(LOCAL_MIN))
            .replace("__MAX__", String(LOCAL_MAX))
            .replace("{{ min }}", String(LOCAL_MIN))
            .replace("{{ max }}", String(LOCAL_MAX)),
        );
        return;
      }
      setFieldError(localEl, "");
    });

    if (fullEl.value) hydrateFromStored(fullEl.value);
    else syncFull();

    if (form) {
      form.addEventListener("submit", (event) => {
        const digits = applyLocal(localEl.value);
        if (!digits.length) {
          setFieldError(localEl, "");
          return;
        }

        const dial = currentDial();
        if (dial === "+90" && digits.length !== TR_LOCAL_LEN) {
          event.preventDefault();
          setFieldError(
            localEl,
            (window.hrcI18n?.phoneTrError || "Enter a 10-digit number for Turkey (e.g. 505 555 55 55)."),
          );
          localEl.focus();
          return;
        }
        if (digits.length < LOCAL_MIN || digits.length > LOCAL_MAX) {
          event.preventDefault();
          setFieldError(
            localEl,
            (window.hrcI18n?.phoneGenericError || "Enter a valid phone number (__MIN__–__MAX__ digits).")
            .replace("__MIN__", String(LOCAL_MIN))
            .replace("__MAX__", String(LOCAL_MAX))
            .replace("{{ min }}", String(LOCAL_MIN))
            .replace("{{ max }}", String(LOCAL_MAX)),
          );
          localEl.focus();
        }
      });
    }
  }

  function initFormGuards(form) {
    form.addEventListener("submit", (event) => {
      let blocked = false;
      form.querySelectorAll("[data-no-digits]").forEach((input) => {
        input.value = stripDigits(input.value);
        if (/\d/.test(input.value)) {
          blocked = true;
          setFieldError(input, (window.hrcI18n?.contactNoDigits || "Numbers are not allowed in this field."));
        }
      });
      if (blocked) {
        event.preventDefault();
        form
          .querySelector("[data-no-digits].contact-page__input--invalid")
          ?.focus();
      }
    });
  }

  function init() {
    document.querySelectorAll("[data-no-digits]").forEach(initNoDigitsField);
    document.querySelectorAll("[data-phone-field]").forEach(initPhoneField);
    const form = document.getElementById("ContactForm");
    if (form) initFormGuards(form);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
